import { inject, Injectable, Renderer2 } from '@angular/core';
import { DeviceDetectorService } from '@core/services/device-detector/device-detector.service';
import { RequestFilesService } from '@core/services/request/request-files.service';
import { PdfViewerService } from '@core/services/pdf-viewer.service';
import { environment } from '@environment/environment';
import { Observable, tap } from 'rxjs';

/**
 * Сервис для скачивания файлов.
 *
 * Стратегии в зависимости от контекста:
 *
 * 1. iOS PWA (standalone-режим на iPhone/iPad):
 *    Новые окна/вкладки использовать нельзя — пользователь теряет контекст PWA.
 *    Для обычных документов используем Web Share API (`navigator.share`).
 *    PDF и изображения, которые нужно держать внутри приложения, должны
 *    открываться отдельными preview-компонентами.
 *
 * 2. Android/Desktop PWA (standalone-режим, не iOS):
 *    `<a download>` работает корректно, но открытие через `target="_blank"` уводит
 *    из PWA-окна. Скачиваем через Blob + `<a download>` программно.
 *
 * 3. Обычный браузер (не PWA):
 *    Классическое поведение через `<a href target="_blank">` или `<a download>`.
 */
@Injectable({
  providedIn: 'root',
})
export class FileDownloadService {
  private readonly deviceDetectorService = inject(DeviceDetectorService);
  private readonly requestFilesService = inject(RequestFilesService);
  private readonly pdfViewerService = inject(PdfViewerService);
  private readonly activeDownloads = new Set<string>();

  /**
   * Таблица соответствия расширений файлов MIME-типам.
   * Используется для корректного определения типа при передаче через Share API на iOS —
   * бэкенд может вернуть `application/octet-stream` даже для PDF/DOCX/XLSX,
   * тогда iOS не распознаёт тип файла и не может предложить нужное приложение.
   */
  private readonly mimeTypeByExtension: Record<string, string> = {
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    doc: 'application/msword',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    zip: 'application/zip',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    txt: 'text/plain',
    csv: 'text/csv',
  };

  /**
   * Скачать или открыть файл.
   *
   * @param href            Полный URL файла (абсолютный или blob:).
   * @param filename        Предлагаемое имя файла для диалога скачивания.
   * @param withoutDownload Если true — файл открывается в новой вкладке
   *                        (например, PDF для предпросмотра). В режиме PWA
   *                        этот флаг игнорируется — файл всегда скачивается,
   *                        чтобы не терять контекст приложения.
   * @param renderer        Экземпляр Renderer2 из вызывающего компонента.
   */
  public downloadFile(
    href: string,
    filename: string,
    withoutDownload = false,
    renderer: Renderer2,
  ): void {
    const normalizedHref = this.normalizeDownloadUrl(href);
    const downloadKey = this.getDownloadKey(
      normalizedHref,
      filename,
      withoutDownload,
    );

    if (!this.acquireDownloadLock(downloadKey)) {
      return;
    }

    const isPwa = this.deviceDetectorService.isInPwaMode;
    const isIOS = this.deviceDetectorService.isIOS;

    if (isPwa && isIOS) {
      void this.downloadViaIOSPwa(normalizedHref, filename).finally(() =>
        this.releaseDownloadLock(downloadKey),
      );
    } else if (isPwa) {
      // Android/Desktop PWA: качаем через Blob, чтобы не уходить из PWA-окна.
      void this.downloadViaBlob(normalizedHref, filename).finally(() =>
        this.releaseDownloadLock(downloadKey),
      );
    } else {
      // Обычный браузер: стандартное поведение через <a>.
      this.downloadViaLink(normalizedHref, filename, withoutDownload, renderer);
      setTimeout(() => this.releaseDownloadLock(downloadKey), 1500);
    }
  }

  public openPdfByPath(path: string, filename: string): void {
    this.openPdfByPath$(path, filename).subscribe();
  }

  public openPdfByPath$(path: string, filename: string): Observable<Blob> {
    return this.requestFilesService.getFileBlobByPath$(path).pipe(
      tap(blob => {
        this.pdfViewerService.open({
          source: blob,
          filename,
        });
      }),
    );
  }

  public isPdfFile(filename: string, href?: string): boolean {
    const normalizedHref = href ? this.normalizeDownloadUrl(href) : '';

    return (
      filename.toLowerCase().endsWith('.pdf') ||
      normalizedHref.toLowerCase().includes('.pdf')
    );
  }

  public openPdfByUrl(href: string, filename: string): void {
    void this.openPdfBlobByUrl(href, filename);
  }

  private getDownloadKey(
    href: string,
    filename: string,
    withoutDownload: boolean,
  ): string {
    return `${href}::${filename}::${withoutDownload}`;
  }

  private acquireDownloadLock(key: string): boolean {
    if (this.activeDownloads.has(key)) {
      return false;
    }

    this.activeDownloads.add(key);

    return true;
  }

  private releaseDownloadLock(key: string): void {
    this.activeDownloads.delete(key);
  }

  private async openPdfBlobByUrl(
    href: string,
    filename: string,
  ): Promise<void> {
    const normalizedHref = this.normalizeDownloadUrl(href);
    const response = await fetch(normalizedHref);

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }

    const blob = await response.blob();

    this.pdfViewerService.open({
      source: blob,
      filename,
    });
  }

  /**
   * Приводит абсолютные ссылки на сервер статики к same-origin proxy `/download-files`.
   * Это нужно для PWA-скачивания через fetch: прямой запрос на `static.*` упрётся в CORS,
   * а через nginx-proxy файл доступен по same-origin URL.
   */
  private normalizeDownloadUrl(href: string): string {
    if (
      !href ||
      href.startsWith('blob:') ||
      href.startsWith('data:') ||
      href.startsWith(environment.downloadFilesUrl)
    ) {
      return href;
    }

    if (href.startsWith('/docs/')) {
      return `${environment.downloadFilesUrl}${href}`;
    }

    try {
      const url = new URL(href, window.location.origin);
      const staticOrigin = new URL(environment.staticUrl).origin;

      if (url.origin === staticOrigin) {
        return `${environment.downloadFilesUrl}${url.pathname}${url.search}`;
      }
    } catch {
      return href;
    }

    return href;
  }

  /**
   * Определяет MIME-тип по расширению из имени файла.
   * Используется для корректной передачи типа в Share API на iOS,
   * когда бэкенд возвращает `application/octet-stream`.
   *
   * @param filename  Имя файла с расширением (например, `report.pdf`).
   * @param fallback  MIME-тип из ответа сервера — используется если расширение неизвестно.
   */
  private getMimeTypeByFilename(
    filename: string,
    fallback = 'application/octet-stream',
  ): string {
    const ext = filename.split('.').pop()?.toLowerCase() ?? '';

    return this.mimeTypeByExtension[ext] ?? fallback;
  }

  /**
   * Стратегия для iOS PWA.
   *
   * На iOS PWA используем только системный Share Sheet, без открытия новых окон.
   */
  private async downloadViaIOSPwa(
    href: string,
    filename: string,
  ): Promise<void> {
    if (typeof navigator.share !== 'function') {
      return;
    }

    const response = await fetch(href);

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }

    const blob = await response.blob();

    // Определяем MIME по расширению файла — бэкенд может прислать octet-stream
    // даже для PDF/DOCX, тогда iOS не распознаёт формат в Share Sheet.
    const mimeType = this.getMimeTypeByFilename(filename, blob.type);
    const typedBlob =
      mimeType !== blob.type ? new Blob([blob], { type: mimeType }) : blob;
    const file = new File([typedBlob], filename, { type: mimeType });

    if (
      typeof navigator.canShare === 'function' &&
      !navigator.canShare({ files: [file] })
    ) {
      return;
    }

    try {
      await navigator.share({ files: [file] });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      throw error;
    }
  }

  /**
   * Стратегия для Android/Desktop PWA.
   * Загружает файл через fetch → Blob → <a download>, не уходя из PWA-окна.
   * revokeObjectURL откладывается на 60 секунд — браузеру нужно время начать скачивание.
   */
  private async downloadViaBlob(href: string, filename: string): Promise<void> {
    const response = await fetch(href);

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }

    const blob = await response.blob();
    // Определяем MIME по расширению — на случай если бэк вернул octet-stream
    const mimeType = this.getMimeTypeByFilename(filename, blob.type);
    const typedBlob =
      mimeType !== blob.type ? new Blob([blob], { type: mimeType }) : blob;
    const objectUrl = URL.createObjectURL(typedBlob);
    const link = document.createElement('a');

    link.href = objectUrl;
    link.download = filename;
    link.click();
    link.remove();

    // Не вызываем revokeObjectURL сразу — браузеру нужно время начать скачивание.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  }

  /**
   * Стратегия для обычного браузера.
   * Открывает файл в новой вкладке или скачивает через <a download>.
   */
  private downloadViaLink(
    href: string,
    filename: string,
    withoutDownload: boolean,
    renderer: Renderer2,
  ): void {
    const link: HTMLAnchorElement = renderer.createElement('a');

    link.setAttribute('target', '_blank');
    link.setAttribute('href', href);
    if (!withoutDownload) {
      link.setAttribute('download', filename);
    }
    link.click();
    link.remove();
  }
}
